import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Text,
  Group,
  Stack,
  Alert,
  ThemeIcon,
  Box,
  Anchor,
  Divider,
  Center
} from '@mantine/core';
import { IconBuilding, IconMail, IconLock, IconAlertCircle, IconEye, IconEyeOff } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

function ModernLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(''); // Clear error when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        // Store token and user data
        localStorage.setItem('token', data.token);
        localStorage.setItem('userFirstName', data.user.firstName);
        localStorage.setItem('userLastName', data.user.lastName);
        localStorage.setItem('userRole', data.user.userRole);

        // Show success notification
        notifications.show({
          title: 'Welcome back!',
          message: `Hello ${data.user.firstName}! You've been logged in successfully.`,
          color: 'green',
        });

        // Navigate to dashboard
        navigate('/dashboard');
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container size={420} my={40}>
      <Stack gap="xl">
        {/* Header */}
        <Center>
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="xl" color="blue" variant="light">
              <IconBuilding size={30} />
            </ThemeIcon>
            <div>
              <Title order={1} ta="center" c="dark">
                Resort Systems
              </Title>
              <Text c="dimmed" size="lg" ta="center">
                Professional Resort Management
              </Text>
            </div>
          </Stack>
        </Center>

        {/* Login Form */}
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Title order={2} ta="center" mb="xl">
            Sign In
          </Title>

          {error && (
            <Alert
              icon={<IconAlertCircle size="1rem" />}
              title="Login Error"
              color="red"
              mb="md"
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Email Address"
                placeholder="Enter your email"
                leftSection={<IconMail size="1rem" />}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                type="email"
                size="md"
              />

              <PasswordInput
                label="Password"
                placeholder="Enter your password"
                leftSection={<IconLock size="1rem" />}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
                size="md"
              />

              <Group justify="space-between" mt="md">
                <Anchor size="sm" href="#" c="dimmed">
                  Forgot password?
                </Anchor>
              </Group>

              <Button
                type="submit"
                fullWidth
                size="md"
                loading={loading}
                mt="xl"
              >
                Sign In
              </Button>
            </Stack>
          </form>

        </Paper>

        {/* Footer */}
        <Text size="sm" c="dimmed" ta="center">
          © 2024 Resort Systems. All rights reserved.
        </Text>
      </Stack>
    </Container>
  );
}

export default ModernLogin;
